import React, { useState } from 'react';
import { Upload, FileCode, CheckCircle, XCircle, Loader, Download, Play } from 'lucide-react';

const BatchProcessor = () => {
  const [files, setFiles] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState(null);

  const mockResults = [
    {
      id: 1,
      filename: 'UserProfile.jsx',
      status: 'success',
      score: 78,
      issues: 5,
      fixable: 4
    },
    {
      id: 2,
      filename: 'Dashboard.jsx',
      status: 'success',
      score: 82,
      issues: 3,
      fixable: 3
    },
    {
      id: 3,
      filename: 'AuthService.js',
      status: 'warning',
      score: 65,
      issues: 8,
      fixable: 5
    },
    {
      id: 4,
      filename: 'api-utils.js',
      status: 'success',
      score: 91,
      issues: 1,
      fixable: 1
    }
  ];

  const handleFileUpload = (e) => {
    const uploadedFiles = Array.from(e?.target?.files || []);
    setFiles(uploadedFiles);
  };

  const handleProcess = () => {
    setProcessing(true);
    // Simulate processing
    setTimeout(() => {
      setResults(mockResults);
      setProcessing(false);
    }, 3000);
  };

  const handleApplyAllFixes = () => {
    console.log('Applying all automatic fixes');
    // Implementation would apply fixes to all files
  };

  const handleExportReport = () => {
    console.log('Exporting batch analysis report');
    // Implementation would generate and download report
  };

  const getStatusConfig = (status) => {
    const configs = {
      success: {
        color: 'text-success',
        bgColor: 'bg-success/10',
        borderColor: 'border-success/20',
        icon: CheckCircle
      },
      warning: {
        color: 'text-warning',
        bgColor: 'bg-warning/10',
        borderColor: 'border-warning/20',
        icon: XCircle
      },
      error: {
        color: 'text-error',
        bgColor: 'bg-error/10',
        borderColor: 'border-error/20',
        icon: XCircle
      }
    };
    return configs?.[status] || configs?.success;
  };

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      <div className="bg-card rounded-xl shadow-sm border border-border p-8">
        <h3 className="text-xl font-semibold text-foreground mb-6">
          Batch Style Analysis
        </h3>
        
        <div className="border-2 border-dashed border-border rounded-xl p-12 text-center hover:border-primary transition-colors">
          <input
            type="file"
            multiple
            accept=".js,.jsx,.ts,.tsx"
            onChange={handleFileUpload}
            className="hidden"
            id="file-upload"
          />
          <label
            htmlFor="file-upload"
            className="cursor-pointer flex flex-col items-center gap-4"
          >
            <div className="p-4 bg-primary/10 rounded-full">
              <Upload className="w-8 h-8 text-primary" />
            </div>
            <div>
              <p className="text-lg font-medium text-foreground mb-1">
                Click to upload or drag and drop
              </p>
              <p className="text-sm text-muted-foreground">
                JavaScript, TypeScript, JSX, or TSX files
              </p>
            </div>
          </label>
        </div>

        {/* Selected Files */}
        {files?.length > 0 && (
          <div className="mt-6 space-y-3">
            <h4 className="font-medium text-foreground">
              Selected Files ({files?.length})
            </h4>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {files?.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-3 bg-muted rounded-lg border border-border"
                >
                  <FileCode className="w-5 h-5 text-primary" />
                  <span className="flex-1 text-sm text-muted-foreground">{file?.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {(file?.size / 1024)?.toFixed(2)} KB
                  </span>
                </div>
              ))}
            </div>
            
            <button
              onClick={handleProcess}
              disabled={processing}
              className="w-full mt-4 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-all flex items-center justify-center gap-2 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {processing ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Processing Files...
                </>
              ) : (
                <>
                  <Play className="w-5 h-5" />
                  Analyze All Files
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Results Section */}
      {results && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-card rounded-xl shadow-sm border border-border p-6">
              <div className="text-3xl font-bold text-foreground">{results?.length}</div>
              <div className="text-sm text-muted-foreground mt-1">Files Analyzed</div>
            </div>
            <div className="bg-success/10 rounded-xl border border-success/20 p-6">
              <div className="text-3xl font-bold text-success">
                {Math.round(
                  results?.reduce((sum, r) => sum + r?.score, 0) / results?.length
                )}%
              </div>
              <div className="text-sm text-success mt-1">Average Score</div>
            </div>
            <div className="bg-warning/10 rounded-xl border border-warning/20 p-6">
              <div className="text-3xl font-bold text-warning">
                {results?.reduce((sum, r) => sum + r?.issues, 0)}
              </div>
              <div className="text-sm text-warning mt-1">Total Issues</div>
            </div>
            <div className="bg-primary/10 rounded-xl border border-primary/20 p-6">
              <div className="text-3xl font-bold text-primary">
                {results?.reduce((sum, r) => sum + r?.fixable, 0)}
              </div>
              <div className="text-sm text-primary mt-1">Auto-Fixable</div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="bg-card rounded-xl shadow-sm border border-border p-6">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-semibold text-foreground">Batch Actions</h4>
                <p className="text-sm text-muted-foreground mt-1">
                  Apply fixes or export detailed report
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleExportReport}
                  className="px-6 py-2 bg-muted text-muted-foreground rounded-lg hover:bg-muted/80 transition-colors flex items-center gap-2 font-medium"
                >
                  <Download className="w-4 h-4" />
                  Export Report
                </button>
                <button
                  onClick={handleApplyAllFixes}
                  className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-all flex items-center gap-2 font-medium"
                >
                  <CheckCircle className="w-4 h-4" />
                  Apply All Fixes
                </button>
              </div>
            </div>
          </div>

          {/* Results Table */}
          <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted border-b border-border">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">
                      File
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">
                      Score
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">
                      Issues
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">
                      Auto-Fixable
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {results?.map((result) => {
                    const statusConfig = getStatusConfig(result?.status);
                    const StatusIcon = statusConfig?.icon;

                    return (
                      <tr key={result?.id} className="hover:bg-muted transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <FileCode className="w-5 h-5 text-muted-foreground" />
                            <span className="font-medium text-foreground">
                              {result?.filename}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <StatusIcon className={`w-5 h-5 ${statusConfig?.color}`} />
                            <span className={`text-sm font-medium ${statusConfig?.color}`}>
                              {result?.status?.charAt(0)?.toUpperCase() + result?.status?.slice(1)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <div className="text-lg font-bold text-foreground">
                              {result?.score}%
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="px-3 py-1 bg-muted text-muted-foreground rounded-full text-sm font-medium">
                            {result?.issues}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="px-3 py-1 bg-success/15 text-success rounded-full text-sm font-medium">
                            {result?.fixable}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <button className="text-primary hover:text-primary font-medium text-sm">
                            View Details
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default BatchProcessor;